/**
 * @file components/ProtectedRoute.jsx
 * @description Route guard that requires an authenticated Azure Entra ID session.
 *
 * Critical MSAL rule: interactive methods (loginRedirect, acquireTokenRedirect)
 * must NEVER be called directly during render. Calling them in render causes
 * MsalProvider to receive a state update while a sibling component is rendering,
 * producing the React warning and the `interaction_in_progress` MSAL error.
 *
 * Solution: call loginRedirect inside a useEffect so it fires AFTER the render
 * cycle completes. The dependency array [isAuthenticated, inProgress, instance]
 * ensures it only fires when the auth state actually changes.
 *
 * @param {Object}          props
 * @param {React.ReactNode} props.children - Page component to render when authenticated.
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useIsAuthenticated, useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';

/**
 * Renders children only when the user is authenticated and MSAL is idle.
 * Shows a spinner during any in-progress MSAL interaction.
 * Navigates to /login if the user is not authenticated, letting them choose
 * when to start the Azure sign-in flow instead of auto-redirecting.
 */
export default function ProtectedRoute({ children }) {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress }  = useMsal();

  // Block rendering while any MSAL interaction is in flight
  if (inProgress !== InteractionStatus.None) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
        <div className="spinner-border" style={{ color: 'var(--color-primary)' }} role="status">
          <span className="visually-hidden">Signing in…</span>
        </div>
      </div>
    );
  }

  // Not authenticated — send to the branded login page
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}
